import { createStore, reconcile, unwrap } from 'solid-js/store';
import { batch, createSignal } from 'solid-js';
import { getSchemaValidator } from 'schemas';
import type { ValidateFunction } from 'ajv';
import { hasStructuralPatchOp } from './patch-structure';

// The Ajv validator (and the schema set it compiles, incl. the vega-lite
// schema) is loaded lazily on first validation so it stays out of the eager
// runtime entry chunk (EID-63). Compiled once, then reused.
const EIDOS_ROOT_SCHEMA_ID = 'https://schemas.oceanum.io/eidos/root.json';

let specValidatePromise: Promise<ValidateFunction> | undefined;
const getSpecValidate = (): Promise<ValidateFunction> => {
  if (!specValidatePromise) {
    specValidatePromise = getSchemaValidator().then((validator) => {
      // The eidos root schema is registered with the validator by $id, so
      // resolve the already-compiled validate function rather than recompiling.
      const validate = validator.getSchema(EIDOS_ROOT_SCHEMA_ID);
      if (!validate) {
        throw new Error(
          `Eidos root schema not registered: ${EIDOS_ROOT_SCHEMA_ID}`,
        );
      }
      return validate as ValidateFunction;
    });
  }
  return specValidatePromise;
};

// Trailing debounce for full-spec AJV validation after spec patches (EID-56).
const SPEC_VALIDATION_DEBOUNCE_MS = 250;

// EID-61: bumped whenever the spec's *node structure* may have changed —
// full spec loads, and patches that can add/remove/move/rename nodes (see
// patch-structure.ts). Content-only patches (scalar replaces) do not bump
// it. Consumers that locate nodes by id across the whole spec (derived-node
// resolution) subscribe to this instead of deep-tracking the entire store.
const [specStructureVersion, setSpecStructureVersion] = createSignal(0);
const bumpSpecStructureVersion = () => setSpecStructureVersion((v) => v + 1);

const [spec, setSpec] = createStore({
  id: '_undefined',
  name: '',
  modalNodes: [],
  root: { id: null, nodeType: null, children: [] },
  data: [],
  transforms: [],
});

// Full-spec validation is expensive (the eidos schema $refs the vega-lite
// schema), so it must never run synchronously on the patch write path.
// specUpdate (full spec load) validates immediately; specPatch schedules a
// trailing debounced validation so a burst of N rapid patches triggers at
// most one full AJV pass. Errors are reported exactly as before (console.log
// per AJV error).
let validationTimer: ReturnType<typeof setTimeout> | undefined;

// Continuous (per-patch) validation can be disabled (e.g. outside
// dev/debug). Validation on full specUpdate always runs.
// EID-109: the Renderer (renderer.tsx) is the caller — it sets this from
// import.meta.env.DEV on mount, so production skips per-patch validation.
// Default stays `true` so anything constructed without going through the
// Renderer (e.g. a future embedding path, or tests) keeps the old behaviour.
let validateOnPatch = true;
const setSpecValidateOnPatch = (enabled: boolean) => {
  validateOnPatch = enabled;
};

const cancelScheduledSpecValidation = () => {
  if (validationTimer !== undefined) {
    clearTimeout(validationTimer);
    validationTimer = undefined;
  }
};

const runSpecValidation = () => {
  if (spec.id === '_undefined') return;
  // The validator loads lazily; validation runs once it resolves. The spec is
  // read at validation time, matching the previous synchronous behaviour for
  // error reporting (console.log per AJV error).
  // On rejection (e.g. network failure loading the schema bundle) we clear the
  // memoised promise so the next patch/update retries the load rather than
  // silently skipping all future validation (EID-67 item 8).
  getSpecValidate()
    .then((specValidate) => {
      if (spec.id === '_undefined') return;
      specValidate(spec) ||
        (specValidate.errors ?? []).map((error) =>
          console.log(
            `Spec does not validate: ${error.instancePath} -> ${error.message} (${JSON.stringify(error.params)})`,
          ),
        );
    })
    .catch((err) => {
      console.error(
        'EID-67: spec validator failed to load — retrying on next validation',
        err,
      );
      specValidatePromise = undefined;
    });
};

const scheduleSpecValidation = () => {
  if (!validateOnPatch) return;
  cancelScheduledSpecValidation();
  validationTimer = setTimeout(() => {
    validationTimer = undefined;
    runSpecValidation();
  }, SPEC_VALIDATION_DEBOUNCE_MS);
};

const specUpdate = (newSpec: typeof spec) => {
  batch(() => {
    setSpec(reconcile(newSpec));
    bumpSpecStructureVersion();
  });
  cancelScheduledSpecValidation();
  runSpecValidation();
};

type JsonPatchOp = { op: string; path: string; value?: unknown };

// move/copy carry no `value` in the JSON-Patch documents the renderer
// receives; applying them here would write `undefined` into the store and
// corrupt the spec. derive.ts (applyPatchOp) also declines them. Warn once
// per op type so a mis-authored patch is visible without log-spamming a burst.
const warnedUnsupportedOps = new Set<string>();
const warnUnsupportedPatchOp = (op: string) => {
  if (warnedUnsupportedOps.has(op)) return;
  warnedUnsupportedOps.add(op);
  console.warn(
    `specPatch: JSON-Patch op '${op}' is not supported and was skipped`,
  );
};

// Read the value at a resolved segment path within a spec object. Used by
// `test` ops to compare against the working copy during the batch dry-run.
const readAt = (root: unknown, segments: (string | number)[]): unknown => {
  let cur: unknown = root;
  for (const seg of segments) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg as string];
  }
  return cur;
};

// JSON-Patch `test`: compare the current value against the op value (both
// stringified, matching derive.ts) and throw on mismatch. Runs during the
// specPatch dry-run; the throw aborts the batch before it commits and the sole
// caller (renderer.tsx) wraps specPatch in try/catch, so throwing is safe.
const assertPatchTest = (
  current: unknown,
  expected: unknown,
  pointer: string,
): void => {
  if (JSON.stringify(current) !== JSON.stringify(expected)) {
    throw new Error(
      `Patch test op failed at '${pointer}': value did not match`,
    );
  }
};

// Apply one JSON-Patch op to a detached working copy (a plain deep clone of the
// spec), mirroring the store-write semantics exactly. `test` ops throw on
// mismatch; move/copy and unrecognised ops warn and no-op. The caller dry-runs
// the whole batch through here before committing, so a throw aborts the batch
// with the live store untouched (RFC 6902 all-or-nothing).
const applyPatchOpToDraft = (
  draft: Record<string, unknown>,
  p: JsonPatchOp,
): void => {
  let path = p.path.split('/').slice(1);
  const p1 = path[path.length - 1];
  const arrayOp = p1 === '-' || Number(p1) >= 0;
  // Array ops address the array via `path` with the trailing index/'-' segment
  // stripped; object ops address the target property directly.
  path = path.slice(0, path.length - (arrayOp ? 1 : 0));

  // move/copy are not implemented (see warnUnsupportedPatchOp) — skip rather
  // than writing undefined into the spec.
  if (p.op === 'move' || p.op === 'copy') {
    warnUnsupportedPatchOp(p.op);
    return;
  }

  if (arrayOp) {
    const arr = readAt(draft, path) as unknown[];
    const idx = Number(p1);
    if (p.op === 'remove') {
      // Out-of-range index removes nothing (matches the prior filter semantics).
      if (Array.isArray(arr) && idx >= 0 && idx < arr.length)
        arr.splice(idx, 1);
    } else if (p.op === 'add') {
      if (p1 === '-') {
        // Append.
        arr.push(p.value);
      } else {
        // Splice-insert: shift existing elements right. A bare index set would
        // REPLACE (destroy) the displaced sibling — the core EID-105 bug.
        arr.splice(idx, 0, p.value);
      }
    } else if (p.op === 'replace') {
      arr[idx] = p.value;
    } else if (p.op === 'test') {
      assertPatchTest(readAt(draft, [...path, idx]), p.value, p.path);
    } else {
      warnUnsupportedPatchOp(p.op);
    }
  } else {
    const parent = readAt(draft, path.slice(0, -1)) as Record<string, unknown>;
    const key = path[path.length - 1];
    if (p.op === 'remove') {
      parent[key] = undefined;
    } else if (p.op === 'add' || p.op === 'replace') {
      parent[key] = p.value;
    } else if (p.op === 'test') {
      assertPatchTest(readAt(draft, path), p.value, p.path);
    } else {
      warnUnsupportedPatchOp(p.op);
    }
  }
};

const specPatch = (patch: JsonPatchOp[]) => {
  // RFC 6902 requires a JSON-Patch batch to apply all-or-nothing. Solid's
  // batch() defers change notifications but does NOT roll back writes, so a
  // failing op mid-batch would otherwise leave earlier ops committed and the
  // renderer's store diverged from the host's authoritative spec. Dry-run the
  // whole batch against a detached working copy first; only commit if every op
  // succeeds. On any failure, surface the error (the renderer's catch only logs
  // a bare exception) and leave the store completely untouched.
  let next: typeof spec;
  try {
    next = structuredClone(unwrap(spec));
    for (const p of patch) {
      applyPatchOpToDraft(next as unknown as Record<string, unknown>, p);
    }
  } catch (err) {
    console.error(
      'specPatch: JSON-Patch batch aborted; no ops applied (RFC 6902 atomicity)',
      err,
    );
    throw err;
  }

  // Entire batch succeeded on the working copy → commit atomically. reconcile
  // preserves node identity where ids match (the EID-105 correctness goal).
  batch(() => {
    setSpec(reconcile(next));
    if (hasStructuralPatchOp(patch)) {
      bumpSpecStructureVersion();
    }
  });
  scheduleSpecValidation();
};

// specTrack performs a deep property walk over a Solid store proxy so that
// Solid's fine-grained reactivity system registers every nested property read
// as a dependency of the enclosing reactive context. The function now returns
// its argument so callers can use the return value (e.g. `specTrack(x)?.prop`)
// without a separate read (EID-67 item 1).
function specTrack<T extends Record<string, unknown>>(store: T): T {
  for (const k in store) {
    const value = store[k];
    if (typeof value === 'object' && value !== null) {
      specTrack(value as Record<string, unknown>);
    }
  }
  return store;
}

export {
  spec,
  specPatch,
  specUpdate,
  specTrack,
  specStructureVersion,
  setSpecValidateOnPatch,
};
